import { useQuery } from '@powersync/react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect, useRef, useState } from 'react';
import { Platform, Text as RNText, View } from 'react-native';
import Animated, { useAnimatedRef } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Sortable from 'react-native-sortables';

import { EditableDescription } from '@/components/domain/EditableDescription';
import { EditableStep } from '@/components/domain/EditableStep';
import { EditableTitle } from '@/components/domain/EditableTitle';
import { NotFoundScreen } from '@/components/domain/NotFoundScreen';
import { SpectatorField } from '@/components/domain/fieldPresence/SpectatorField';
import { SpectatorStep } from '@/components/domain/fieldPresence/SpectatorStep';
import { TemplateFieldPresence } from '@/components/domain/fieldPresence/TemplateFieldPresence';
import { ShareSheet } from '@/components/domain/ShareSheet';
import { SyncStatusArea } from '@/components/domain/syncStatus/SyncStatusArea';
import { Bounded } from '@/components/ui/Bounded';
import { BoundedKeyboardAwareScrollView } from '@/components/ui/BoundedKeyboardAwareScrollView';
import { Button } from '@/components/ui/Button';
import { Divider } from '@/components/ui/Divider';
import { EmojiPicker } from '@/components/ui/EmojiPicker';
import { NavBar } from '@/components/ui/NavBar';
import { Screen } from '@/components/ui/Screen';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { StatusBanner } from '@/components/ui/StatusBanner';
import { Surface } from '@/components/ui/Surface';
import { Text } from '@/components/ui/Text';
import { TextField } from '@/components/ui/TextField';
import { useAuth } from '@/lib/auth';
import { confirmDestructive } from '@/lib/confirm';
import {
  addTemplateItem,
  deleteTemplate,
  deleteTemplateItem,
  reorderTemplateItems,
  updateTemplate,
  type Template,
  type TemplateItem,
} from '@/lib/data';
import { hapticPickUp, hapticReorderTick, hapticSetDown } from '@/lib/haptics';
import { useGoBack } from '@/lib/navigation';
import { DESCRIPTION_FIELD_KEY, TITLE_FIELD_KEY } from '@/lib/presence/fieldKeys';
import { useInFlightGuard } from '@/lib/useInFlightGuard';
import { useStartRun } from '@/lib/useStartRun';
import { type AccessRole, type TemplateRole } from '@rbf/shared';

export default function TemplateEditorScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const goBack = useGoBack('/');
  const { session } = useAuth();
  const insets = useSafeAreaInsets();
  const me = session?.user.id;

  const { data: templates } = useQuery<Template>(
    'SELECT * FROM templates WHERE id = ? AND deleted_at IS NULL',
    [id],
  );
  const { data: items } = useQuery<TemplateItem>(
    'SELECT * FROM template_items WHERE template_id = ? ORDER BY position, id',
    [id],
  );
  // The viewer's own membership row (empty for the owner) tells us their role,
  // which gates editing and sharing. It syncs, so it's available offline.
  const { data: myCollab } = useQuery<{ role: TemplateRole }>(
    'SELECT role FROM template_collaborators WHERE template_id = ? AND user_id = ?',
    [id, me ?? ''],
  );
  const template = templates[0] ?? null;

  const role: AccessRole | null = !template
    ? null
    : template.owner_id === me
      ? 'owner'
      : (myCollab[0]?.role ?? null);
  const isOwner = role === 'owner';
  const canAdmin = role === 'owner' || role === 'admin';
  const canEdit = canAdmin || role === 'editor';

  const scrollableRef = useAnimatedRef<Animated.ScrollView>();

  const [newItem, setNewItem] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Suspends the pull-to-peek pan (Android) while a sortable drag owns the
  // touch, so pulling a step downward from the top never opens the banner.
  const [reordering, setReordering] = useState(false);
  const { startRun: beginRun, starting, error: runError } = useStartRun();
  const runAddItem = useInFlightGuard();
  const runDelete = useInFlightGuard();

  const [shareOpen, setShareOpen] = useState(false);

  // Keyboard reorder plumbing: a screen-reader announcement of the last move, and
  // the id of the step whose handle should regain focus once the reordered list
  // re-renders (so a user can press ArrowUp repeatedly to walk a step upward). The
  // pending id is a ref, not state — it must not trigger a render, and the effect
  // already re-runs when the reordered `items` array arrives.
  const [announcement, setAnnouncement] = useState('');
  const pendingFocusId = useRef<string | null>(null);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const focusId = pendingFocusId.current;
    if (!focusId) return;
    pendingFocusId.current = null;
    document.getElementById(`step-handle-${focusId}`)?.focus();
  }, [items]);

  // Move a step one slot and persist. Reuses reorderTemplateItems (the drag path)
  // by handing it the array with the item spliced into its new spot; the reactive
  // `ORDER BY position` query then re-renders the grid.
  function moveItem(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(index, 1);
    if (!moved) return;
    next.splice(target, 0, moved);
    pendingFocusId.current = moved.id;
    setAnnouncement(
      `Moved "${moved.label || 'step'}" to position ${target + 1} of ${items.length}`,
    );
    reorderTemplateItems(next).catch((e) => setError((e as Error).message));
  }

  async function addItem() {
    await runAddItem(async () => {
      const label = newItem.trim();
      if (!label) return;
      try {
        await addTemplateItem({ template_id: id, label });
        setNewItem('');
      } catch (e) {
        setError((e as Error).message);
      }
    });
  }

  async function startRun() {
    // UI-level guard so we don't even attempt an empty run (startInstance also
    // rejects it as a backstop); the shared hook handles the run-limit/paywall,
    // spin-up, navigation, busy flag, and error.
    if (items.length === 0) {
      setError('Add at least one step before starting a run.');
      return;
    }
    setError(null);
    await beginRun(id);
  }

  async function confirmDelete() {
    await runDelete(async () => {
      if (
        !(await confirmDestructive(
          'Delete template?',
          'Past runs of this template stay in your history.',
        ))
      ) {
        return;
      }
      try {
        await deleteTemplate(id);
        router.replace('/');
      } catch (e) {
        setError((e as Error).message);
      }
    });
  }

  if (!template) {
    return (
      <NotFoundScreen
        onBack={goBack}
        navTitle="Template"
        title="Checklist not found"
        body="This checklist may have been deleted, or you don't have access to it."
      />
    );
  }

  return (
    <Screen edges={['top']}>
      <TemplateFieldPresence templateId={id} userId={me ?? null} canEdit={canEdit}>
        <NavBar onBack={goBack} title={canEdit ? 'Edit template' : 'Template'} />

        <SyncStatusArea scrollRef={scrollableRef} peekEnabled={!reordering}>
          <BoundedKeyboardAwareScrollView
            scrollRef={scrollableRef}
            alwaysBounceVertical
            contentClassName="gap-4 px-5 pb-10 pt-1"
          >
            {!canEdit ? (
              <StatusBanner
                variant="caution"
                icon="dot"
                title="Shared template"
                subtitle="You can run this template and view its history."
              />
            ) : null}

            {/* Icon + title */}
            <View className="flex-row items-center gap-3">
              {canEdit ? (
                <EmojiPicker onChange={(emoji) => updateTemplate(id, { icon: emoji })}>
                  <View className="h-14 w-14 items-center justify-center rounded-2xl bg-well">
                    <RNText className="text-3xl">{template.icon ?? '📋'}</RNText>
                  </View>
                </EmojiPicker>
              ) : (
                <View className="h-14 w-14 items-center justify-center rounded-2xl bg-well">
                  <RNText className="text-3xl">{template.icon ?? '📋'}</RNText>
                </View>
              )}
              <View className="flex-1">
                {canEdit ? (
                  <EditableTitle
                    key={template.id}
                    initial={template.title}
                    onCommit={(t) => updateTemplate(id, { title: t })}
                  />
                ) : (
                  <SpectatorField fieldKey={TITLE_FIELD_KEY} className="self-start">
                    <Text variant="title">{template.title}</Text>
                  </SpectatorField>
                )}
              </View>
            </View>

            {/* Description */}
            {canEdit ? (
              <EditableDescription
                key={`${template.id}-desc`}
                initial={template.description}
                onCommit={(d) => updateTemplate(id, { description: d })}
              />
            ) : (
              <SpectatorField
                fieldKey={DESCRIPTION_FIELD_KEY}
                onlyWhenClaimed={!template.description}
                className="self-start"
              >
                {template.description ? (
                  <Text variant="body" className="text-foreground-secondary">
                    {template.description}
                  </Text>
                ) : null}
              </SpectatorField>
            )}

            <SectionHeader label="Steps" count={items.length} className="mt-2" />
            {canEdit ? (
              <Text variant="caption" className="-mt-1 text-faint">
                Reference notes show during runs
              </Text>
            ) : null}

            {/* Off-screen live region: announces keyboard reorder moves to screen
            readers (accessibilityLiveRegion maps to aria-live on web). */}
            <Text
              accessibilityLiveRegion="polite"
              style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}
            >
              {announcement}
            </Text>

            <Surface className="px-3 py-1">
              {items.length === 0 ? (
                <Text variant="caption" className="px-1 py-4 text-center">
                  No steps yet. Add the things you check off, in the order you do them.
                </Text>
              ) : canEdit ? (
                <Sortable.Grid
                  columns={1}
                  data={items}
                  keyExtractor={(item) => item.id}
                  customHandle
                  dragActivationDelay={0}
                  activeItemScale={1.02}
                  scrollableRef={scrollableRef}
                  rowGap={0}
                  onDragStart={() => {
                    setReordering(true);
                    hapticPickUp();
                  }}
                  onOrderChange={() => hapticReorderTick()}
                  onDragEnd={({ data }) => {
                    setReordering(false);
                    hapticSetDown();
                    reorderTemplateItems(data).catch((e) =>
                      setError((e as Error).message),
                    );
                  }}
                  renderItem={({ item, index }) => (
                    // bg-card keeps the row opaque while the library lifts it out of
                    // the Surface during a drag (otherwise the active row is see-through).
                    <View className="bg-card">
                      {index > 0 ? <Divider /> : null}
                      <EditableStep
                        item={item}
                        position={index + 1}
                        onRemove={() => deleteTemplateItem(item.id)}
                        onMoveUp={() => moveItem(index, -1)}
                        onMoveDown={() => moveItem(index, 1)}
                        canMoveUp={index > 0}
                        canMoveDown={index < items.length - 1}
                      />
                    </View>
                  )}
                />
              ) : (
                items.map((item, i) => (
                  <View key={item.id}>
                    {i > 0 ? <Divider /> : null}
                    <SpectatorStep item={item} />
                  </View>
                ))
              )}
            </Surface>

            {canEdit ? (
              <View className="flex-row gap-2">
                <TextField
                  className="flex-1"
                  value={newItem}
                  onChangeText={setNewItem}
                  placeholder="Add a step"
                  onSubmitEditing={addItem}
                  returnKeyType="done"
                />
                <Button label="Add" variant="outline" onPress={addItem} />
              </View>
            ) : null}

            {/* the screen's own errors + run-start errors from useStartRun */}
            {(error ?? runError) ? (
              <Text variant="caption" className="text-alert">
                {error ?? runError}
              </Text>
            ) : null}
          </BoundedKeyboardAwareScrollView>
        </SyncStatusArea>

        {/* Border + background span the full window; only the controls are
          capped/centered so the divider reads as a full-width separator. */}
        <View className="border-t border-border bg-background">
          <Bounded
            className="gap-2 px-5 pt-3"
            style={{ paddingBottom: insets.bottom + 8 }}
          >
            <Button label="Start run" busy={starting} onPress={startRun} />
            {canAdmin ? (
              <View className="flex-row gap-2">
                <Button
                  className="flex-1"
                  variant="outline"
                  label="Share"
                  onPress={() => setShareOpen(true)}
                />
                {isOwner ? (
                  <Button
                    className="flex-1"
                    variant="ghost"
                    label="Delete"
                    onPress={confirmDelete}
                  />
                ) : null}
              </View>
            ) : null}
          </Bounded>
        </View>

        <ShareSheet
          visible={shareOpen}
          onClose={() => setShareOpen(false)}
          templateId={id}
        />
      </TemplateFieldPresence>
    </Screen>
  );
}
